import React, { useState, useEffect } from 'react';

interface Segment {
    segment: string;
    count: number;
    type?: string;
}

interface Contact {
    email: string;
    first_name?: string;
    last_name?: string;
    company?: string;
    segments?: string[];
    status: string;
}

interface ImportResult {
    total_processed: number;
    scrubbed_count: number;
    imported_count: number;
    duplicate_count: number;
    scrubbed_emails: string[];
    scrub_breakdown?: { [key: string]: number };
    domains_validated?: number;
    execution_time?: string;
    segment?: string;
    queued?: boolean;
    jobId?: string;
    message?: string;
    estimatedTime?: string;
}

interface Notification {
    id: string;
    type: 'success' | 'info' | 'warning' | 'error';
    title: string;
    message: string;
    timestamp: Date;
    autoHide?: boolean;
}

interface ImportJob {
    job_id: string;
    segment: string;
    status: string;
    file_name?: string;
    file_size_kb?: number;
    total_emails: number;
    imported_count: number;
    duplicate_count: number;
    scrubbed_count: number;
    invalid_count: number;
    created_at: string;
    started_at?: string;
    completed_at?: string;
    execution_time_seconds?: number;
    reviewed: boolean;
    reviewed_at?: string;
    error_message?: string;
    scrub_breakdown?: { [key: string]: number };
}

const EmailContactsManager: React.FC = () => {
    const [viewMode, setViewMode] = useState<'segments' | 'search' | 'import' | 'imports'>('segments');
    const [segments, setSegments] = useState<Segment[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Contact[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalContacts, setTotalContacts] = useState(0);
    const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
    const [unreviewedCount, setUnreviewedCount] = useState(0);

    // Segment display limit
    const [segmentDisplayLimit, setSegmentDisplayLimit] = useState<number>(25);

    // Search filters
    const [searchSegmentFilter, setSearchSegmentFilter] = useState('');
    const [searchType, setSearchType] = useState<'contacts' | 'segments'>('contacts');
    const [segmentSearchResults, setSegmentSearchResults] = useState<Segment[]>([]);

    // Import state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [segmentInput, setSegmentInput] = useState('');
    const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
    const [isNewSegment, setIsNewSegment] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [segmentSearchQuery, setSegmentSearchQuery] = useState('');
    const [showSegmentDropdown, setShowSegmentDropdown] = useState(false);

    // Notification state
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const addNotification = (type: 'success' | 'info' | 'warning' | 'error', title: string, message: string, autoHide = true) => {
        const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const notification = {
            id,
            type,
            title,
            message,
            timestamp: new Date(),
            autoHide
        };

        setNotifications(prev => [...prev, notification]);

        if (autoHide) {
            setTimeout(() => {
                setNotifications(prev => prev.filter(n => n.id !== id));
            }, 5000);
        }
    };

    const removeNotification = (id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const API_BASE = 'https://xijjve6puc6ht4inqmmgbss4ay0earbn.lambda-url.us-east-1.on.aws';
    const API_BASE_POSTGRES = 'https://xijjve6puc6ht4inqmmgbss4ay0earbn.lambda-url.us-east-1.on.aws';

    // Load segments on mount
    useEffect(() => {
        loadSegments();
        loadTotalCount();
        if (viewMode === 'imports') {
            loadImportHistory();
        }
    }, [viewMode]);


    const loadTotalCount = async () => {
        try {
            const response = await fetch(`${API_BASE_POSTGRES}/contacts/total-count`);
            if (response.ok) {
                const data = await response.json();
                setTotalContacts(data.total || 0);
            }
        } catch (error) {
            console.error('Error loading total count:', error);
        }
    };

    const loadSegments = async () => {
        setIsLoading(true);
        try {
            // Use the same API endpoint as campaign dashboard to get consistent segment list
            const response = await fetch(`${API_BASE_POSTGRES}/contacts/segments`);
            if (response.ok) {
                const data = await response.json();
                setSegments(data.segments || []);
            }
        } catch (error) {
            console.error('Error loading segments:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = async () => {
        if (searchType === 'segments') {
            // Search segments locally - filter the segments list
            setIsLoading(true);
            try {
                const query = searchQuery.trim().toLowerCase();
                if (query) {
                    const filtered = segments.filter(seg => 
                        seg.segment.toLowerCase().includes(query)
                    );
                    setSegmentSearchResults(filtered);
                } else {
                    // If no query, show all segments
                    setSegmentSearchResults(segments);
                }
            } catch (error) {
                console.error('Error searching segments:', error);
            } finally {
                setIsLoading(false);
            }
        } else {
            // Search contacts via API
            if (!searchQuery.trim() && !searchSegmentFilter) {
                return;
            }

            setIsLoading(true);
            try {
                const searchParams: any = {};

                if (searchQuery.trim()) {
                    searchParams.query = searchQuery.trim();
                }

                if (searchSegmentFilter) {
                    searchParams.segment = searchSegmentFilter;
                }

                const response = await fetch(`${API_BASE}/contacts/search`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(searchParams)
                });

                if (response.ok) {
                    const data = await response.json();
                    setSearchResults(data.contacts || []);
                }
            } catch (error) {
                console.error('Error searching contacts:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const loadImportHistory = async () => {
        try {
            const response = await fetch(`${API_BASE_POSTGRES}/imports`);
            if (response.ok) {
                const data = await response.json();
                setImportJobs(data.imports || []);
            }

            // Load unreviewed count
            const unreviewedRes = await fetch(`${API_BASE_POSTGRES}/imports/unreviewed-count`);
            if (unreviewedRes.ok) {
                const data = await unreviewedRes.json();
                setUnreviewedCount(data.count || 0);
            }
        } catch (error) {
            console.error('Error loading import history:', error);
        }
    };

    const markImportReviewed = async (jobId: string) => {
        try {
            const response = await fetch(`${API_BASE_POSTGRES}/imports/${jobId}/review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (response.ok) {
                setImportJobs(prev => prev.map(job => 
                    job.job_id === jobId ? { ...job, reviewed: true } : job
                ));
                setUnreviewedCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error marking import as reviewed:', error);
        }
    };

    const handleImportsTabClick = async () => {
        setViewMode('imports');
        
        // Load import history and get the jobs
        const response = await fetch(`${API_BASE_POSTGRES}/imports`);
        if (response.ok) {
            const data = await response.json();
            const jobs = data.imports || [];
            setImportJobs(jobs);
            
            // Mark all visible unreviewed imports as reviewed
            const unreviewed = jobs.filter((job: ImportJob) => !job.reviewed);
            for (const job of unreviewed) {
                await markImportReviewed(job.job_id);
            }
        }
        
        // Also load unreviewed count
        const unreviewedRes = await fetch(`${API_BASE_POSTGRES}/imports/unreviewed-count`);
        if (unreviewedRes.ok) {
            const data = await unreviewedRes.json();
            setUnreviewedCount(data.count || 0);
        }
    };

    const handleImport = async () => {
        if (!selectedFile) {
            alert('Please select a CSV file to import');
            return;
        }

        // Determine which segments to use
        let segmentsToImport: string[] = [];
        if (isNewSegment) {
            if (!segmentInput.trim()) {
                alert('Please enter a segment name');
                return;
            }
            segmentsToImport = [segmentInput.trim()];
        } else {
            if (selectedSegments.length === 0) {
                alert('Please select at least one segment');
                return;
            }
            segmentsToImport = selectedSegments;
        }

        setIsLoading(true);
        setImportResult(null);

        try {
            // Read file as text
            const fileText = await selectedFile.text();

            const response = await fetch(`${API_BASE}/contacts/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    csv_data: fileText,
                    segments: segmentsToImport // Send as array
                })
            });

            if (response.ok) {
                const data = await response.json();
                setImportResult(data);

                if (data.queued && data.jobId) {
                    addNotification('info', 'Import Queued',
                        `Large import queued for background processing. Job ID: ${data.jobId}. Estimated time: ${data.estimatedTime}`,
                        false
                    );
                    // Poll for completion
                    pollImportJob(data.jobId);
                } else if (data.queued) {
                    addNotification('info', 'Import Queued',
                        `Import queued for background processing. Estimated time: ${data.estimatedTime}`,
                        false
                    );
                } else {
                    addNotification('success', 'Import Complete',
                        `Processed: ${data.total_processed}, Imported: ${data.imported_count}, Scrubbed: ${data.scrubbed_count}`
                    );
                }

                // Refresh segments, total count, and import history
                await loadSegments();
                await loadTotalCount();
                await loadImportHistory();

                // Clear form
                setSelectedFile(null);
                setSegmentInput('');
                setSelectedSegments([]);
            } else {
                const errorData = await response.json();
                addNotification('error', 'Import Failed', errorData.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error importing contacts:', error);
            addNotification('error', 'Import Error', 'Failed to import contacts. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const pollImportJob = (jobId: string) => {
        const interval = setInterval(async () => {
            try {
                const response = await fetch(`${API_BASE_POSTGRES}/imports/${jobId}`);
                if (!response.ok) return;

                const data = await response.json();
                const job = data.job;
                if (!job) return;

                if (job.status === 'completed') {
                    clearInterval(interval);
                    // Remove the queued notification
                    setNotifications(prev => prev.filter(n => !n.message.includes(jobId)));
                    addNotification('success', 'Import Complete',
                        `Imported ${Number(job.imported_count || 0).toLocaleString()} contacts. ` +
                        `Scrubbed: ${Number(job.scrubbed_count || 0).toLocaleString()}, ` +
                        `Duplicates: ${Number(job.duplicate_count || 0).toLocaleString()}.`,
                        false
                    );
                    await loadSegments();
                    await loadTotalCount();
                    await loadImportHistory();
                } else if (job.status === 'failed') {
                    clearInterval(interval);
                    setNotifications(prev => prev.filter(n => !n.message.includes(jobId)));
                    addNotification('error', 'Import Failed',
                        job.error_message || 'The import failed. Check Import History for details.',
                        false
                    );
                    await loadImportHistory();
                }
            } catch (error) {
                console.error('Error polling import job:', error);
            }
        }, 5000);
    };

    return (
        <div className="email-contacts-manager">
            <div className="contacts-header">
                <div>
                    <h1>📧 Email Contacts</h1>
                    <p className="header-subtitle">Manage and search contacts in PostgreSQL database</p>
                </div>
                <div className="header-stats">
                    <div className="stat-badge">
                        <span className="stat-label">Total Contacts:</span>
                        <span className="stat-value">{totalContacts.toLocaleString()}</span>
                    </div>
                    <div className="stat-badge">
                        <span className="stat-label">Segments:</span>
                        <span className="stat-value">{segments.length}</span>
                    </div>
                </div>
            </div>

            {/* View Mode Tabs */}
            <div className="view-tabs">
                <button
                    className={`tab ${viewMode === 'segments' ? 'active' : ''}`}
                    onClick={() => setViewMode('segments')}
                >
                    📊 Segments
                </button>
                <button
                    className={`tab ${viewMode === 'search' ? 'active' : ''}`}
                    onClick={() => setViewMode('search')}
                >
                    🔍 Search Contacts
                </button>
                <button
                    className={`tab ${viewMode === 'import' ? 'active' : ''}`}
                    onClick={() => setViewMode('import')}
                >
                    📤 Import Contacts
                </button>
                <button
                    className={`tab ${viewMode === 'imports' ? 'active' : ''}`}
                    onClick={handleImportsTabClick}
                    style={{ position: 'relative' }}
                >
                    📊 Import History
                    {unreviewedCount > 0 && (
                        <span className="notification-badge">{unreviewedCount}</span>
                    )}
                </button>
            </div>

            {/* Notifications */}
            {notifications.length > 0 && (
                <div className="notifications-container">
                    {notifications.map(notification => (
                        <div key={notification.id} className={`notification notification-${notification.type}`}>
                            <div className="notification-content">
                                <div className="notification-title">{notification.title}</div>
                                <div className="notification-message">{notification.message}</div>
                            </div>
                            <button
                                className="notification-close"
                                onClick={() => removeNotification(notification.id)}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Segments View */}
            {viewMode === 'segments' && (
                <div className="segments-view">
                    <div className="view-header">
                        <h2>Contact Segments</h2>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <label style={{ fontSize: '14px', color: '#666' }}>Show:</label>
                            <select
                                value={segmentDisplayLimit}
                                onChange={(e) => setSegmentDisplayLimit(Number(e.target.value))}
                                style={{ padding: '5px 10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ddd' }}
                            >
                                <option value={25}>25 segments</option>
                                <option value={50}>50 segments</option>
                                <option value={9999}>All segments</option>
                            </select>
                            <button
                                className="btn-refresh"
                                onClick={loadSegments}
                                disabled={isLoading}
                            >
                                {isLoading ? '⏳ Loading...' : '🔄 Refresh'}
                            </button>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="loading-state">
                            <p>⏳ Loading segments...</p>
                        </div>
                    ) : segments.length === 0 ? (
                        <div className="empty-state">
                            <p>No segments found</p>
                        </div>
                    ) : (
                        <>
                            {segmentDisplayLimit < segments.length && (
                                <div style={{ padding: '10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', marginBottom: '10px', fontSize: '14px', color: '#0369a1' }}>
                                    📊 Showing {segmentDisplayLimit} of {segments.length} segments
                                </div>
                            )}
                            <div className="table-container">
                                <table className="segments-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Segment Name</th>
                                            <th>Contact Count</th>
                                            <th>Percentage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {segments.slice(0, segmentDisplayLimit).map((segment, idx) => {
                                            const percentage = totalContacts > 0
                                                ? ((segment.count / totalContacts) * 100).toFixed(2)
                                                : '0.00';

                                            const icon = segment.type === 'state' ? '🗺️' : '📋';

                                            return (
                                                <tr key={`${segment.type}-${segment.segment}`}>
                                                    <td>{idx + 1}</td>
                                                    <td className="segment-name">
                                                        {icon} <strong>{segment.segment || '(No segment)'}</strong>
                                                    </td>
                                                    <td className="count-cell">
                                                        {segment.count.toLocaleString()}
                                                    </td>
                                                    <td className="percentage-cell">
                                                        <div className="percentage-bar">
                                                            <div
                                                                className="percentage-fill"
                                                                style={{ width: `${Math.min(parseFloat(percentage), 100)}%` }}
                                                            />
                                                        </div>
                                                        <span className="percentage-text">{percentage}%</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Search View */}
            {viewMode === 'search' && (
                <div className="search-view">
                    <div className="view-header">
                        <h2>Search {searchType === 'contacts' ? 'Contacts' : 'Segments'}</h2>
                    </div>

                    {/* Search Type Toggle */}
                    <div className="search-type-toggle" style={{ marginBottom: '20px', padding: '15px', background: '#f9fafb', borderRadius: '8px' }}>
                        <label style={{ display: 'flex', gap: '20px', alignItems: 'center', fontSize: '14px', fontWeight: 500 }}>
                            <span>Search Type:</span>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="searchType"
                                    value="contacts"
                                    checked={searchType === 'contacts'}
                                    onChange={(e) => {
                                        setSearchType('contacts');
                                        setSearchResults([]);
                                        setSegmentSearchResults([]);
                                    }}
                                />
                                <span>📧 Contacts</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="searchType"
                                    value="segments"
                                    checked={searchType === 'segments'}
                                    onChange={(e) => {
                                        setSearchType('segments');
                                        setSearchResults([]);
                                        // Auto-show all segments when switching to segment search
                                        setSegmentSearchResults(segments);
                                    }}
                                />
                                <span>📊 Segments</span>
                            </label>
                        </label>
                    </div>

                    {searchType === 'contacts' && (
                        <div className="search-filters">
                            <div className="filter-group">
                                <label>Filter by Segment:</label>
                                <select
                                    value={searchSegmentFilter}
                                    onChange={(e) => setSearchSegmentFilter(e.target.value)}
                                    className="segment-filter"
                                >
                                    <option value="">All Segments</option>
                                    {segments.map(seg => (
                                        <option key={seg.segment} value={seg.segment}>
                                            {seg.segment} ({seg.count.toLocaleString()})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="search-box">
                        <input
                            type="text"
                            placeholder={searchType === 'contacts' 
                                ? "Enter email address or search term (optional)..." 
                                : "Enter segment name to search..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            className="search-input"
                        />
                        <button
                            className="btn-search"
                            onClick={handleSearch}
                            disabled={isLoading || (searchType === 'contacts' && !searchQuery.trim() && !searchSegmentFilter) || (searchType === 'segments' && !searchQuery.trim())}
                        >
                            {isLoading ? '⏳ Searching...' : '🔍 Search'}
                        </button>
                    </div>

                    {/* Contact Search Results */}
                    {searchType === 'contacts' && searchResults.length > 0 && (
                        <div className="search-results">
                            <h3>Found {searchResults.length} contact{searchResults.length !== 1 ? 's' : ''}</h3>
                            <div className="table-container">
                                <table className="contacts-table">
                                    <thead>
                                        <tr>
                                            <th>Email</th>
                                            <th>Name</th>
                                            <th>Company</th>
                                            <th>Segments</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {searchResults.map((contact, idx) => (
                                            <tr key={idx}>
                                                <td className="email-cell">{contact.email}</td>
                                                <td>{contact.first_name} {contact.last_name}</td>
                                                <td>{contact.company || '-'}</td>
                                                <td>{contact.segments && contact.segments.length > 0 ? contact.segments.join(', ') : '-'}</td>
                                                <td>
                                                    <span className={`status-badge status-${contact.status}`}>
                                                        {contact.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Segment Search Results */}
                    {searchType === 'segments' && segmentSearchResults.length > 0 && (
                        <div className="search-results">
                            <h3>Found {segmentSearchResults.length} segment{segmentSearchResults.length !== 1 ? 's' : ''}</h3>
                            <div className="table-container">
                                <table className="segments-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Segment Name</th>
                                            <th>Contact Count</th>
                                            <th>Percentage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {segmentSearchResults.map((segment, idx) => {
                                            const percentage = totalContacts > 0
                                                ? ((segment.count / totalContacts) * 100).toFixed(2)
                                                : '0.00';
                                            const icon = segment.type === 'state' ? '🗺️' : '📋';
                                            return (
                                                <tr key={`${segment.type}-${segment.segment}`}>
                                                    <td>{idx + 1}</td>
                                                    <td className="segment-name">
                                                        {icon} <strong>{segment.segment || '(No segment)'}</strong>
                                                    </td>
                                                    <td className="count-cell">
                                                        {segment.count.toLocaleString()}
                                                    </td>
                                                    <td className="percentage-cell">
                                                        <div className="percentage-bar">
                                                            <div
                                                                className="percentage-fill"
                                                                style={{ width: `${Math.min(parseFloat(percentage), 100)}%` }}
                                                            />
                                                        </div>
                                                        <span className="percentage-text">{percentage}%</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* No Results Message */}
                    {((searchType === 'contacts' && searchQuery.trim() && searchResults.length === 0 && !isLoading) ||
                      (searchType === 'segments' && searchQuery.trim() && segmentSearchResults.length === 0 && !isLoading)) && (
                        <div className="empty-state" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                            <p>No {searchType} found matching "{searchQuery}"</p>
                        </div>
                    )}
                </div>
            )}

            {/* Import View */}
            {viewMode === 'import' && (
                <div className="import-view">
                    <div className="view-header">
                        <h2>Import Contacts</h2>
                    </div>

                    <div className="import-form">
                        <div className="form-section">
                            <h3>1. Select CSV File</h3>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                className="file-input"
                            />
                            {selectedFile && (
                                <div className="file-info">
                                    📄 Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(0)} KB)
                                </div>
                            )}
                            <div className="help-text">
                                💡 Maximum file size: 5 MB (approximately 50,000 contacts)
                            </div>
                        </div>

                        <div className="form-section">
                            <h3>2. Select or Enter Segment</h3>
                            <div className="segment-selector">
                                <label>
                                    <input
                                        type="radio"
                                        checked={!isNewSegment}
                                onChange={() => {
                                    setIsNewSegment(false);
                                    setShowSegmentDropdown(false);
                                    setSegmentSearchQuery('');
                                    setSelectedSegments([]);
                                    setSegmentInput('');
                                }}
                                    />
                                    <span>Use Existing Segment</span>
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        checked={isNewSegment}
                                onChange={() => {
                                    setIsNewSegment(true);
                                    setShowSegmentDropdown(false);
                                    setSegmentSearchQuery('');
                                    setSelectedSegments([]);
                                    setSegmentInput('');
                                }}
                                    />
                                    <span>Create New Segment</span>
                                </label>
                            </div>

                            {!isNewSegment ? (
                                <div style={{ position: 'relative' }}>
                                    <div 
                                        style={{
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            padding: '10px',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            minHeight: '42px'
                                        }}
                                        onClick={() => setShowSegmentDropdown(!showSegmentDropdown)}
                                    >
                                        <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                                            {selectedSegments.length > 0 ? (
                                                selectedSegments.map(seg => {
                                                    const segData = segments.find(s => s.segment === seg);
                                                    return (
                                                        <span
                                                            key={seg}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                padding: '4px 8px',
                                                                backgroundColor: '#eff6ff',
                                                                border: '1px solid #3b82f6',
                                                                borderRadius: '4px',
                                                                fontSize: '13px',
                                                                color: '#1e40af',
                                                                fontWeight: 500
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedSegments(selectedSegments.filter(s => s !== seg));
                                                            }}
                                                        >
                                                            {seg}
                                                            {segData && <span style={{ fontSize: '11px', opacity: 0.7 }}>({segData.count.toLocaleString()})</span>}
                                                            <span style={{ cursor: 'pointer', fontSize: '14px', marginLeft: '2px' }}>×</span>
                                                        </span>
                                                    );
                                                })
                                            ) : (
                                                <span style={{ color: '#9ca3af' }}>-- Select Segment(s) --</span>
                                            )}
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>▼</span>
                                    </div>
                                    {showSegmentDropdown && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            marginTop: '4px',
                                            backgroundColor: 'white',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                            zIndex: 1000,
                                            maxHeight: '300px',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}>
                                            <div style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                                                <input
                                                    type="text"
                                                    placeholder="🔍 Search segments..."
                                                    value={segmentSearchQuery}
                                                    onChange={(e) => setSegmentSearchQuery(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '4px',
                                                        fontSize: '14px'
                                                    }}
                                                    autoFocus
                                                />
                                            </div>
                                            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                {segments
                                                    .filter(seg => 
                                                        segmentSearchQuery === '' || 
                                                        seg.segment.toLowerCase().includes(segmentSearchQuery.toLowerCase())
                                                    )
                                                    .map(seg => {
                                                        const isSelected = selectedSegments.includes(seg.segment);
                                                        return (
                                                            <div
                                                                key={seg.segment}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (isSelected) {
                                                                        setSelectedSegments(selectedSegments.filter(s => s !== seg.segment));
                                                                    } else {
                                                                        setSelectedSegments([...selectedSegments, seg.segment]);
                                                                    }
                                                                }}
                                                                style={{
                                                                    padding: '12px 16px',
                                                                    cursor: 'pointer',
                                                                    borderBottom: '1px solid #f3f4f6',
                                                                    backgroundColor: isSelected ? '#eff6ff' : 'white',
                                                                    transition: 'background-color 0.2s',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '12px'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!isSelected) {
                                                                        e.currentTarget.style.backgroundColor = '#f9fafb';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!isSelected) {
                                                                        e.currentTarget.style.backgroundColor = 'white';
                                                                    }
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => {}}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                                />
                                                                <div style={{ flex: 1 }}>
                                                                    <div style={{ fontWeight: isSelected ? 600 : 400, color: '#1f2937' }}>
                                                                        {seg.segment}
                                                                    </div>
                                                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                                                        {seg.count.toLocaleString()} contacts
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                {segments.filter(seg => 
                                                    segmentSearchQuery === '' || 
                                                    seg.segment.toLowerCase().includes(segmentSearchQuery.toLowerCase())
                                                ).length === 0 && (
                                                    <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                                                        No segments found
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {showSegmentDropdown && (
                                        <div
                                            style={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                zIndex: 999
                                            }}
                                            onClick={() => {
                                                setShowSegmentDropdown(false);
                                                setSegmentSearchQuery('');
                                            }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    placeholder="Enter new segment name (e.g., webinar_2025)"
                                    value={segmentInput}
                                    onChange={(e) => setSegmentInput(e.target.value)}
                                    className="form-input"
                                />
                            )}
                        </div>

                        <div className="form-actions">
                            <button
                                className="btn-import"
                                onClick={handleImport}
                                disabled={isLoading || !selectedFile || (isNewSegment ? !segmentInput.trim() : selectedSegments.length === 0)}
                            >
                                {isLoading ? '⏳ Importing & Scrubbing...' : '📤 Import Contacts'}
                            </button>
                        </div>
                    </div>

                    {/* Import Results */}
                    {importResult && (
                        <div className="import-results">
                            {importResult.queued ? (
                                <>
                                    <h3>📤 Import Queued</h3>
                                    <div className="results-summary">
                                        <div className="result-card info">
                                            <div className="result-label">Job ID</div>
                                            <div className="result-value" style={{ fontSize: '16px', wordBreak: 'break-all' }}>
                                                {importResult.jobId}
                                            </div>
                                        </div>
                                        <div className="result-card warning">
                                            <div className="result-label">Estimated Time</div>
                                            <div className="result-value">{importResult.estimatedTime}</div>
                                        </div>
                                    </div>
                                    <div className="import-meta">
                                        <div>📊 Segment: <strong>{importResult.segment}</strong></div>
                                        <div>ℹ️ Status: <strong>Processing in background</strong></div>
                                        <div>💡 You'll receive a notification when complete</div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h3>✅ Import Complete!</h3>
                                    <div className="results-summary">
                                        <div className="result-card">
                                            <div className="result-label">Total Processed</div>
                                            <div className="result-value">{importResult.total_processed.toLocaleString()}</div>
                                        </div>
                                        <div className="result-card success">
                                            <div className="result-label">✅ Imported (Clean)</div>
                                            <div className="result-value">{importResult.imported_count.toLocaleString()}</div>
                                        </div>
                                        <div className="result-card warning">
                                            <div className="result-label">🗑️ Scrubbed (Removed)</div>
                                            <div className="result-value">{importResult.scrubbed_count.toLocaleString()}</div>
                                        </div>
                                        <div className="result-card info">
                                            <div className="result-label">🔄 Duplicates</div>
                                            <div className="result-value">{importResult.duplicate_count.toLocaleString()}</div>
                                        </div>
                                    </div>

                                    {importResult.scrub_breakdown && (
                                        <div className="scrub-breakdown">
                                            <h4>Scrubbing Breakdown:</h4>
                                            <ul>
                                                {Object.entries(importResult.scrub_breakdown).map(([reason, count]) => (
                                                    <li key={reason}>
                                                        <strong>{reason.replace(/_/g, ' ')}:</strong> {count as number}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    <div className="import-meta">
                                        <div>📊 Segment: <strong>{importResult.segment}</strong></div>
                                        <div>⏱️ Execution Time: <strong>{importResult.execution_time}s</strong></div>
                                        {importResult.domains_validated && (
                                            <div>🌐 Domains Validated: <strong>{importResult.domains_validated}</strong></div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Import History View */}
            {viewMode === 'imports' && (
                <div className="imports-view">
                    <div className="view-header">
                        <h2>📊 Import History</h2>
                        <button
                            className="btn-refresh"
                            onClick={loadImportHistory}
                            disabled={isLoading}
                        >
                            {isLoading ? '⏳ Loading...' : '🔄 Refresh'}
                        </button>
                    </div>
                    {importJobs.length === 0 ? (
                        <div className="empty-state">
                            <p>No import history yet.</p>
                        </div>
                    ) : (
                        <div className="imports-list">
                            {importJobs.map(job => {
                                const isComplete = job.status === 'completed';
                                const isFailed = job.status === 'failed';
                                const isProcessing = job.status === 'processing';

                                return (
                                    <div key={job.job_id} className={`import-card ${!job.reviewed ? 'unreviewed' : ''}`}>
                                        <div className="import-header">
                                            <h3>📂 {job.segment}</h3>
                                            <span className={`status-badge status-${job.status}`}>
                                                {isProcessing ? '🔄 Processing' : 
                                                 isComplete ? '✅ Completed' : 
                                                 isFailed ? '❌ Failed' : job.status}
                                            </span>
                                        </div>

                                        <div className="import-meta">
                                            {job.file_name && <div>📄 File: {job.file_name}</div>}
                                            {job.file_size_kb && <div>💾 Size: {Number(job.file_size_kb).toFixed(2)} KB</div>}
                                            <div>📅 Started: {new Date(job.created_at).toLocaleString()}</div>
                                            {job.completed_at && (
                                                <div>⏱️ Completed: {new Date(job.completed_at).toLocaleString()}</div>
                                            )}
                                            {job.execution_time_seconds && (
                                                <div>⚡ Time: {Number(job.execution_time_seconds).toFixed(2)}s</div>
                                            )}
                                        </div>

                                        {isComplete && (
                                            <div className="import-results">
                                                <h4>Results:</h4>
                                                <div className="results-grid">
                                                    <div className="result-item success">
                                                        <span className="result-label">✅ Imported</span>
                                                        <span className="result-value">{Number(job.imported_count || 0).toLocaleString()}</span>
                                                    </div>
                                                    <div className="result-item info">
                                                        <span className="result-label">🔄 Updated</span>
                                                        <span className="result-value">{Number(job.duplicate_count || 0).toLocaleString()}</span>
                                                    </div>
                                                    <div className="result-item warning">
                                                        <span className="result-label">🚫 Scrubbed</span>
                                                        <span className="result-value">{Number(job.scrubbed_count || 0).toLocaleString()}</span>
                                                    </div>
                                                    <div className="result-item error">
                                                        <span className="result-label">❌ Invalid</span>
                                                        <span className="result-value">{Number(job.invalid_count || 0).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                                <div className="result-summary">
                                                    Total Processed: {Number(job.total_emails || 0).toLocaleString()} emails
                                                </div>
                                                {(() => {
                                                    // Parse scrub_breakdown if it's a string (JSON from PostgreSQL)
                                                    let breakdown: { [key: string]: number } | null = job.scrub_breakdown || null;
                                                    if (typeof breakdown === 'string') {
                                                        try {
                                                            breakdown = JSON.parse(breakdown);
                                                        } catch (e) {
                                                            breakdown = null;
                                                        }
                                                    }
                                                    return breakdown && typeof breakdown === 'object' && Object.keys(breakdown).length > 0 && (
                                                        <div className="scrub-breakdown" style={{ marginTop: '15px', padding: '15px', background: '#f9fafb', borderRadius: '6px' }}>
                                                            <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>🧹 Scrub Breakdown:</h5>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                                                                {Object.entries(breakdown).map(([reason, count]) => (
                                                                    Number(count) > 0 && (
                                                                        <div key={reason} style={{ fontSize: '13px', color: '#4b5563' }}>
                                                                            <strong>{reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {Number(count)}
                                                                        </div>
                                                                    )
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        )}

                                        {isFailed && job.error_message && (
                                            <div className="import-error">
                                                ❌ Error: {job.error_message}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <style>{`
                .email-contacts-manager {
                    padding: 20px;
                    max-width: 1400px;
                    margin: 0 auto;
                }

                .contacts-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid #e5e7eb;
                }

                .contacts-header h1 {
                    margin: 0 0 8px 0;
                    color: #1f2937;
                    font-size: 32px;
                }

                .header-subtitle {
                    color: #6b7280;
                    margin: 0;
                    font-size: 16px;
                }

                .header-stats {
                    display: flex;
                    gap: 15px;
                }

                .stat-badge {
                    background: white;
                    padding: 12px 20px;
                    border-radius: 8px;
                    border: 2px solid #e5e7eb;
                }

                .stat-label {
                    font-size: 12px;
                    color: #6b7280;
                    margin-right: 8px;
                }

                .stat-value {
                    font-size: 20px;
                    font-weight: 700;
                    color: #3b82f6;
                }

                .view-tabs {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 20px;
                }

                .tab {
                    padding: 12px 24px;
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 16px;
                    font-weight: 600;
                    color: #6b7280;
                    transition: all 0.2s;
                }

                .tab:hover {
                    border-color: #3b82f6;
                    color: #3b82f6;
                }

                .tab.active {
                    background: #3b82f6;
                    color: white;
                    border-color: #3b82f6;
                }

                .segments-view, .search-view {
                    background: white;
                    border-radius: 8px;
                    padding: 30px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }

                .view-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .view-header h2 {
                    margin: 0;
                    color: #1f2937;
                }

                .btn-refresh {
                    padding: 10px 20px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 600;
                }

                .btn-refresh:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }

                .search-box {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 20px;
                }

                .search-input {
                    flex: 1;
                    padding: 12px 16px;
                    border: 2px solid #d1d5db;
                    border-radius: 8px;
                    font-size: 16px;
                }

                .search-input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .btn-search {
                    padding: 12px 32px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 16px;
                    font-weight: 600;
                    white-space: nowrap;
                }

                .btn-search:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }

                .table-container {
                    overflow-x: auto;
                }

                .segments-table, .contacts-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .segments-table thead, .contacts-table thead {
                    background: #f9fafb;
                    border-bottom: 2px solid #e5e7eb;
                }

                .segments-table th, .contacts-table th {
                    padding: 12px 16px;
                    text-align: left;
                    font-weight: 600;
                    color: #374151;
                    font-size: 14px;
                }

                .segments-table tbody tr, .contacts-table tbody tr {
                    border-bottom: 1px solid #e5e7eb;
                    transition: background 0.2s;
                }

                .segments-table tbody tr:hover, .contacts-table tbody tr:hover {
                    background: #f9fafb;
                }

                .segments-table td, .contacts-table td {
                    padding: 12px 16px;
                    font-size: 14px;
                    color: #1f2937;
                }

                .segment-name {
                    color: #1f2937;
                    font-weight: 600;
                }

                .count-cell {
                    text-align: right;
                    font-weight: 700;
                    color: #3b82f6;
                    font-size: 16px;
                }

                .percentage-cell {
                    min-width: 200px;
                }

                .percentage-bar {
                    height: 8px;
                    background: #e5e7eb;
                    border-radius: 4px;
                    overflow: hidden;
                    margin-bottom: 4px;
                }

                .percentage-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
                    transition: width 0.3s ease;
                }

                .percentage-text {
                    font-size: 12px;
                    color: #6b7280;
                    font-weight: 600;
                }

                .email-cell {
                    font-family: monospace;
                    color: #3b82f6;
                    font-size: 13px;
                }

                .status-badge {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .status-active {
                    background: #dcfce7;
                    color: #166534;
                }

                .status-suppressed {
                    background: #fee2e2;
                    color: #991b1b;
                }

                .loading-state, .empty-state {
                    padding: 60px 20px;
                    text-align: center;
                    color: #6b7280;
                    font-size: 16px;
                }

                .search-results {
                    margin-top: 20px;
                }

                .search-results h3 {
                    margin: 0 0 15px 0;
                    color: #1f2937;
                }

                /* Import View */
                .import-view, .search-view {
                    background: white;
                    border-radius: 8px;
                    padding: 30px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }

                .import-form {
                    max-width: 800px;
                }

                .form-section {
                    margin-bottom: 30px;
                    padding: 20px;
                    background: #f9fafb;
                    border-radius: 8px;
                }

                .form-section h3 {
                    margin: 0 0 15px 0;
                    color: #1f2937;
                    font-size: 18px;
                }

                .file-input {
                    display: block;
                    padding: 10px;
                    border: 2px dashed #d1d5db;
                    border-radius: 8px;
                    width: 100%;
                    cursor: pointer;
                }

                .file-info {
                    margin-top: 10px;
                    padding: 10px;
                    background: #eff6ff;
                    border: 1px solid #3b82f6;
                    border-radius: 6px;
                    color: #1e40af;
                }

                .help-text {
                    margin-top: 8px;
                    font-size: 14px;
                    color: #6b7280;
                }

                .segment-selector {
                    display: flex;
                    gap: 20px;
                    margin-bottom: 15px;
                }

                .segment-selector label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                }

                .form-select, .form-input {
                    width: 100%;
                    padding: 10px 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    font-size: 14px;
                }

                .form-actions {
                    margin-top: 20px;
                }

                .btn-import {
                    padding: 14px 32px;
                    background: #10b981;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 16px;
                    font-weight: 600;
                }

                .btn-import:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }

                .import-results {
                    margin-top: 30px;
                    padding: 30px;
                    background: #f0fdf4;
                    border: 2px solid #10b981;
                    border-radius: 12px;
                }

                .import-results h3 {
                    margin: 0 0 20px 0;
                    color: #166534;
                    font-size: 24px;
                }

                .results-summary {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 15px;
                    margin-bottom: 20px;
                }

                .result-card {
                    padding: 20px;
                    background: white;
                    border-radius: 8px;
                    border: 2px solid #e5e7eb;
                    text-align: center;
                }

                .result-card.success {
                    border-color: #10b981;
                    background: #f0fdf4;
                }

                .result-card.warning {
                    border-color: #f59e0b;
                    background: #fffbeb;
                }

                .result-card.info {
                    border-color: #3b82f6;
                    background: #eff6ff;
                }

                .result-label {
                    font-size: 14px;
                    color: #6b7280;
                    margin-bottom: 8px;
                }

                .result-value {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1f2937;
                }

                .scrub-breakdown {
                    margin-top: 20px;
                    padding: 20px;
                    background: white;
                    border-radius: 8px;
                }

                .scrub-breakdown h4 {
                    margin: 0 0 10px 0;
                    color: #1f2937;
                }

                .scrub-breakdown ul {
                    margin: 0;
                    padding-left: 20px;
                }

                .scrub-breakdown li {
                    margin-bottom: 8px;
                    color: #374151;
                }

                .import-meta {
                    margin-top: 20px;
                    padding: 15px;
                    background: white;
                    border-radius: 8px;
                    display: flex;
                    gap: 20px;
                    flex-wrap: wrap;
                    font-size: 14px;
                    color: #6b7280;
                }

                /* Notifications */
                .notifications-container {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 1000;
                    max-width: 400px;
                }

                .notification {
                    display: flex;
                    align-items: flex-start;
                    padding: 16px;
                    margin-bottom: 12px;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    animation: slideIn 0.3s ease-out;
                }

                .notification-success {
                    background: #f0fdf4;
                    border-left: 4px solid #10b981;
                    color: #166534;
                }

                .notification-info {
                    background: #eff6ff;
                    border-left: 4px solid #3b82f6;
                    color: #1e40af;
                }

                .notification-warning {
                    background: #fffbeb;
                    border-left: 4px solid #f59e0b;
                    color: #92400e;
                }

                .notification-error {
                    background: #fef2f2;
                    border-left: 4px solid #ef4444;
                    color: #dc2626;
                }

                .notification-content {
                    flex: 1;
                }

                .notification-title {
                    font-weight: 600;
                    font-size: 14px;
                    margin-bottom: 4px;
                }

                .notification-message {
                    font-size: 13px;
                    opacity: 0.9;
                }

                .notification-close {
                    background: none;
                    border: none;
                    font-size: 18px;
                    cursor: pointer;
                    opacity: 0.6;
                    margin-left: 8px;
                }

                .notification-close:hover {
                    opacity: 1;
                }

                .notification-badge {
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    background: #ef4444;
                    color: white;
                    border-radius: 12px;
                    padding: 2px 8px;
                    font-size: 11px;
                    font-weight: 700;
                    min-width: 20px;
                    text-align: center;
                }

                /* Import History Styles */
                .imports-view {
                    background: white;
                    border-radius: 8px;
                    padding: 30px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }

                .imports-list {
                    display: grid;
                    gap: 20px;
                    margin-top: 20px;
                }

                .import-card {
                    padding: 20px;
                    background: #f9fafb;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    transition: all 0.2s;
                }

                .import-card.unreviewed {
                    border-color: #3b82f6;
                    background: #eff6ff;
                }

                .import-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 15px;
                }

                .import-header h3 {
                    margin: 0;
                    color: #1f2937;
                }

                .import-meta {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 10px;
                    margin-bottom: 15px;
                    font-size: 14px;
                    color: #6b7280;
                }

                .import-results {
                    margin-top: 15px;
                    padding: 15px;
                    background: white;
                    border-radius: 6px;
                }

                .import-results h4 {
                    margin: 0 0 15px 0;
                    color: #1f2937;
                }

                .results-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                    gap: 15px;
                    margin-bottom: 15px;
                }

                .result-item {
                    padding: 15px;
                    background: #f9fafb;
                    border-radius: 6px;
                    text-align: center;
                    border: 2px solid #e5e7eb;
                }

                .result-item.success {
                    border-color: #10b981;
                    background: #f0fdf4;
                }

                .result-item.info {
                    border-color: #3b82f6;
                    background: #eff6ff;
                }

                .result-item.warning {
                    border-color: #f59e0b;
                    background: #fffbeb;
                }

                .result-item.error {
                    border-color: #ef4444;
                    background: #fef2f2;
                }

                .result-label {
                    display: block;
                    font-size: 12px;
                    color: #6b7280;
                    margin-bottom: 5px;
                }

                .result-value {
                    display: block;
                    font-size: 24px;
                    font-weight: 700;
                    color: #1f2937;
                }

                .result-summary {
                    text-align: center;
                    padding: 10px;
                    background: #f9fafb;
                    border-radius: 6px;
                    font-weight: 600;
                    color: #374151;
                }

                .import-error {
                    margin-top: 15px;
                    padding: 15px;
                    background: #fef2f2;
                    border: 2px solid #ef4444;
                    border-radius: 6px;
                    color: #dc2626;
                    font-weight: 600;
                }

                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
};

export default EmailContactsManager;
